import { useRef, useState } from 'react';

interface IProps {
  intervalDuration: number;
  onFinishCountdown?: () => void;
}

const padNumber = (seconds: number): string | null => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');

  const timerText = `${formattedMinutes}:${formattedSeconds}`;

  if (timerText === '00:00') {
    return null;
  } else {
    return timerText;
  }
};

export const useCountdownTimer = ({ intervalDuration, onFinishCountdown }: IProps) => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const timerId = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadTimeStamp = useRef<number | null>(null);
  const didFinishCounting = useRef(false);

  function startTimer(timestamp?: number) {
    /** Reset */
    timestamp = timestamp || Date.now();
    loadTimeStamp.current = timestamp!;
    didFinishCounting.current = false;
    const initialElapsedTimeInSeconds = (Date.now() - loadTimeStamp.current) / 1000;
    setTimeLeft(Number((intervalDuration - initialElapsedTimeInSeconds).toFixed(0)));

    timerId.current = setInterval(() => {
      if (didFinishCounting.current) {
        return;
      }

      const elapsedTimeInSeconds = loadTimeStamp.current
        ? (Date.now() - loadTimeStamp.current) / 1000
        : 0;

      setTimeLeft(Number((intervalDuration - elapsedTimeInSeconds).toFixed(0)));

      if (Math.max(intervalDuration - elapsedTimeInSeconds, 0) === 0) {
        onFinishCountdown?.();
        setTimeLeft(null);
        if (timerId.current !== null) {
          clearInterval(timerId.current);
        }
        didFinishCounting.current = true;
        loadTimeStamp.current = null;
      }
    }, 1000);
  }

  return { startTimer, timeLeft, padTimeLeft: padNumber(timeLeft || 0) };
};
